import type { Maybe } from '@adornis/base/utilTypes.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { useHorizontalDragResize } from '@adornis/dragdrop/useDragResize.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { MapBox } from '@adornis/maps/client/components/MapBox.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { differenceInMinutes, format } from 'date-fns';
import { de } from 'date-fns/locale';
import { html } from 'lit';
import type mapboxgl from 'mapbox-gl';
import { calculateStats } from '../api/calculateRouteOptimizationStats.js';
import { dedupeDepotStops } from '../api/dedupeDepotStops.js';
import { getAreaFromVehicleId } from '../api/getAreaFromVehicleId.js';
import { getEngineerProfitabilitySummary } from '../api/getEngineerProfitability.js';
import {
  getFieldEngineersWithoutRoutes,
  getFieldEngineersWithoutRoutesWithCoords,
} from '../api/getFieldEngineersWithoutRoutes.js';
import { getFreeAvailableWorkingTimeMinutes } from '../api/getFreeAvailableWorkingTimeMinutes.js';
import { getTotalProfitabilitySummary } from '../api/getRouteOptimizationProfitability.js';
import { getRouteRevenueByEngineer } from '../api/getRouteRevenueByEngineer.js';
import { getTicketRevenue } from '../api/getTicketRevenue.js';
import { getTotalServiceTime } from '../api/getTotalServiceTime.js';
import { getTotalTravelDistanceKm } from '../api/getTotalTravelDistanceKm.js';
import { getTotalTravelTime } from '../api/getTotalTravelTime.js';
import { getWorkingTimeMinutes } from '../api/getWorkingTimeMinutes.js';
import { VEHICLE_MAX_ROUTE_DURATION_MINUTES } from '../api/optimizeRoute.js';
import { subscribePlanningPudoLocations } from '../api/subscribe-planning-pudo-locations.js';
import { subscribeRoutePlanningById } from '../api/subscribe-route-planning-by-id.js';
import { subscribeRoutePlannings } from '../api/subscribe-route-plannings.js';
import { routeOptimizationConfig } from '../config.js';
import type { PudoLocation } from '../entities/PudoLocation.js';
import { RouteOptimizationResult } from '../entities/RouteOptimizationResult.js';
import type { RouteTicketAssigned } from '../entities/RouteTicket.js';
import { getTicketDurationMinutes } from '../get-ticket-duration.js';
import { VEHICLE_COST_PER_KILOMETER } from '../shared/pricing.js';
import './Marker.js';
import { extractIDOnlyFromFEKey } from '../api/extractIDOnlyFromFEKey.js';

const ticketsBaseUrl =
  String(routeOptimizationConfig.get('TICKETS_BASE_URL') ?? '').replace(/\/$/, '') || 'https://tickets.nscglobal.com';

function formatTravelTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours > 0) {
    return `${hours}h ${mins}min`;
  }
  return `${mins}min`;
}

function formatDistanceKm(km: number): string {
  return `${km.toFixed(1)} km`;
}

function formatFixedFEStatus(ticket: unknown): string {
  if (!ticket || typeof ticket !== 'object') return 'No';
  const fixedFE = (ticket as { fixedFE?: unknown }).fixedFE;
  return fixedFE === true ? 'Yes' : 'No';
}

function getExpectedFELabel(ticket: unknown, fieldEngineerLabelById: ReadonlyMap<string, string>): string {
  if (!ticket || typeof ticket !== 'object') return 'N/A';
  const fixedFE = (ticket as { fixedFE?: unknown }).fixedFE;
  if (fixedFE !== true) return 'N/A';
  const employeeId = (ticket as { employeeId?: unknown }).employeeId;
  const normalizedEmployeeId = typeof employeeId === 'string' ? employeeId.trim() : '';
  if (!normalizedEmployeeId) return 'Unknown';
  return fieldEngineerLabelById.get(normalizedEmployeeId.toLowerCase()) ?? normalizedEmployeeId;
}

function normalizeLookupKey(value: string | null | undefined): string | undefined {
  const normalizedValue = typeof value === 'string' ? extractIDOnlyFromFEKey(value) : '';
  return normalizedValue || undefined;
}

function getTicketTypeBadgeColor(ticketType: string | null | undefined): string {
  switch (ticketType) {
    case 'Client':
      return '#2196f3';
    case 'Enterprise':
      return '#9c27b0';
    case 'HES':
      return '#4caf50';
    default:
      return '#ff9800';
  }
}

function formatTicketLocation(ticket: {
  address?: string | null;
  zip?: string | null;
  city?: string | null;
}): string | undefined {
  const location =
    `${ticket.address ?? ''}${ticket.zip || ticket.city ? `, ${ticket.zip ?? ''} ${ticket.city ?? ''}` : ''}`.trim();
  return location || undefined;
}

function getStopAddress(
  stop: { address?: string | null; ticketId?: string | null },
  selectedPlanning: Maybe<RouteOptimizationResult>,
): string | undefined {
  if (stop.address) return stop.address;
  if (!stop.ticketId || !selectedPlanning) return undefined;
  const allTickets = [...(selectedPlanning.ticketsToSchedule ?? []), ...(selectedPlanning.ticketsWithFETomorrow ?? [])];
  const ticket = allTickets.find(t => t.id === stop.ticketId);
  return ticket?.address ?? undefined;
}

function formatTicketTimeframe(ticket: {
  startTime?: string | Date | null;
  endTime?: string | Date | null;
}): string | undefined {
  const formattedStartTime = ticket.startTime
    ? format(new Date(ticket.startTime), 'dd.MM.yyyy, HH:mm', { locale: de })
    : undefined;
  const formattedEndTime = ticket.endTime
    ? format(new Date(ticket.endTime), 'dd.MM.yyyy, HH:mm', { locale: de })
    : undefined;
  if (formattedStartTime && formattedEndTime) return `${formattedStartTime} - ${formattedEndTime}`;
  return formattedStartTime ?? formattedEndTime;
}

function renderSidebarTicketSection(
  title: string,
  tickets: Array<{
    id?: string | null;
    number?: string | null;
    type?: string | null;
    address?: string | null;
    zip?: string | null;
    city?: string | null;
    pudoName?: string | null;
    fixedFE?: boolean | null;
    employeeId?: string | null;
    startTime?: string | Date | null;
    endTime?: string | Date | null;
  }>,
  fieldEngineerLabelById: ReadonlyMap<string, string>,
  emptyLabel: string,
) {
  return html`
    <div style="margin-top: 12px; border-top: 1px solid #444; padding-top: 8px;">
      <div
        style="font-size: 11px; font-weight: 600; color: #999; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;"
      >
        ${title} (${tickets.length})
      </div>
      ${tickets.length
        ? html`<div style="display: flex; flex-direction: column; gap: 8px;">
            ${tickets.map(
              ticket => html`
                <div
                  style="background: #3d3d3d; border-radius: 8px; padding: 10px; display: flex; flex-direction: column; gap: 4px;"
                >
                  <div style="display: flex; justify-content: space-between; align-items: center; gap: 8px;">
                    <div style="display: flex; align-items: center; gap: 8px; min-width: 0;">
                      <span style="font-weight: 600; color: #fff;">${ticket.number ?? ticket.id ?? 'N/A'}</span>
                      ${ticket.id
                        ? html`<a
                            href=${`${ticketsBaseUrl}/otrs/index.pl?Action=AgentTicketZoom;TicketID=${encodeURIComponent(
                              String(ticket.id),
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style="color: #2196f3; text-decoration: none; font-size: 11px;"
                            >Open ticket</a
                          >`
                        : ''}
                    </div>
                    <span
                      style="font-size: 11px; padding: 2px 8px; border-radius: 4px; background: ${getTicketTypeBadgeColor(
                        ticket.type,
                      )}; color: white;"
                      >${ticket.type ?? 'Unknown'}</span
                    >
                  </div>
                  ${formatTicketLocation(ticket)
                    ? html`<div style="font-size: 12px; color: #aaa;">${formatTicketLocation(ticket)}</div>`
                    : ''}
                  ${formatTicketTimeframe(ticket)
                    ? html`<div style="font-size: 11px; color: #888; display: flex; gap: 8px; align-items: center;">
                        <span style="font-weight: 600; color: #999;">Timeframe</span>
                        <span>${formatTicketTimeframe(ticket)}</span>
                      </div>`
                    : ''}
                  ${ticket.pudoName
                    ? html`<div style="font-size: 11px; color: #888; display: flex; gap: 8px; align-items: center;">
                        <span style="font-weight: 600; color: #999;">PUDO</span>
                        <span>${ticket.pudoName}</span>
                      </div>`
                    : ''}
                  <div style="font-size: 11px; color: #888; display: flex; gap: 8px; align-items: center;">
                    <span style="font-weight: 600; color: #999;">Fixed FE</span>
                    <span>${formatFixedFEStatus(ticket)}</span>
                  </div>
                  ${formatFixedFEStatus(ticket) === 'Yes'
                    ? html`<div style="font-size: 11px; color: #888; display: flex; gap: 8px; align-items: center;">
                        <span style="font-weight: 600; color: #999;">Expected FE</span>
                        <span>${getExpectedFELabel(ticket, fieldEngineerLabelById)}</span>
                      </div>`
                    : ''}
                </div>
              `,
            )}
          </div>`
        : html`<div style="font-size: 11px; color: #777;">${emptyLabel}</div>`}
    </div>
  `;
}

function inferDepotNameFromStop(
  stop: { type?: string | null; address?: string | null; ticketId?: string | null },
  depotNameByTicketId: ReadonlyMap<string, string>,
): string | undefined {
  if (stop.ticketId) {
    const depotName = depotNameByTicketId.get(stop.ticketId);
    if (depotName) return depotName;
  }
  if (stop.type !== 'depot' || !stop.address) return undefined;
  return stop.address.split(',')[0]?.trim() || stop.address;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

const getImacTicketsForEngineer = (selectedPlanning: Maybe<RouteOptimizationResult>, feID: Maybe<string>) =>
  selectedPlanning && feID
    ? (selectedPlanning?.ticketsWithFETomorrow ?? []).filter(ticket => {
        const employeeKey = normalizeLookupKey(ticket.employeeId);
        if (!employeeKey || employeeKey !== normalizeLookupKey(feID)) return false;
        if (ticket.type !== 'IMAC') return false;
        return !!ticket.id;
      })
    : [];
const getAssignedTicketsForEngineer = (selectedPlanning: Maybe<RouteOptimizationResult>, feID: Maybe<string>) =>
  selectedPlanning && feID
    ? (selectedPlanning.ticketsWithFETomorrow ?? []).filter(ticket => {
        const employeeKey = normalizeLookupKey(ticket.employeeId);
        if (!employeeKey || employeeKey !== normalizeLookupKey(feID)) return false;
        if (ticket.type === 'IMAC') return false;
        return !!ticket.id;
      })
    : [];
const getFixedFETicketsForEngineer = (selectedPlanning: Maybe<RouteOptimizationResult>, feID: Maybe<string>) =>
  selectedPlanning && feID
    ? (selectedPlanning?.fixedFETickets ?? []).filter(ticket => {
        const employeeKey = normalizeLookupKey(ticket.employeeId);
        return !!employeeKey && employeeKey === normalizeLookupKey(feID);
      })
    : [];

function renderMarkerPopupContent(options: {
  title: string;
  rows: Array<{ label: string; value?: string | number | null | undefined }>;
}) {
  return html`
    <div style="display: flex; flex-direction: column; gap: 6px; min-width: 220px; color: #111;">
      <div style="font-size: 13px; font-weight: 700; color: #111;">${options.title}</div>
      <div style="display: flex; flex-direction: column; gap: 2px; font-size: 12px; color: #444;">
        ${options.rows
          .filter(row => row.value !== undefined && row.value !== null && row.value !== '')
          .map(
            row => html`
              <div>
                <span style="font-weight: 600; color: #222;">${row.label}:</span>
                <span>${String(row.value)}</span>
              </div>
            `,
          )}
      </div>
    </div>
  `;
}

function renderProfitabilityBreakdownDialog(
  breakdown: {
    name: string;
    id?: string | null;
    profitability: ReturnType<typeof getEngineerProfitabilitySummary>;
    ticketTypeBreakdown?: Record<string, number>;
    availableTimeMinutes?: number | null;
    isTotal?: boolean;
  },
  onClose: () => void,
) {
  const { name, id, profitability, ticketTypeBreakdown, availableTimeMinutes, isTotal } = breakdown;
  const availableTime = availableTimeMinutes ?? 0;
  const assignedTime = profitability.routeMinutes ?? 0;
  return html`
    <div
      style="position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1002;"
      @click=${(e: Event) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style="background: #2d2d2d; border-radius: 12px; padding: 24px; max-width: 480px; max-height: 80vh; overflow-y: auto; color: white; min-width: 320px;"
      >
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
          <h3 style="margin: 0; font-size: 18px; display: flex; align-items: center; gap: 8px;">
            <span class="icon-[material-symbols--account-balance-wallet]" style="color: #2196f3;"></span>
            Profitability Breakdown
          </h3>
          <button
            style="background: transparent; border: none; color: #888; cursor: pointer; padding: 4px;"
            @click=${onClose}
          >
            <span class="icon-[material-symbols--close]"></span>
          </button>
        </div>
        <div style="margin-bottom: 16px;">
          <div style="font-weight: 600; font-size: 16px;">${name}</div>
          ${id && id !== name ? html`<div style="font-size: 11px; color: #888;">${id}</div>` : ''}
          ${!isTotal
            ? html`<span
                style="display: inline-block; margin-top: 6px; font-size: 11px; padding: 2px 8px; border-radius: 4px; background: ${profitability.isPartnerFieldEngineer
                  ? '#9c27b0'
                  : '#2196f3'}; color: white;"
                >${profitability.isPartnerFieldEngineer ? 'Partner' : 'Internal'}</span
              >`
            : ''}
        </div>
        <div style="display: flex; flex-direction: column; gap: 8px;">
          ${ticketTypeBreakdown && Object.keys(ticketTypeBreakdown).length
            ? html`<div style="padding: 10px 12px; background: #3d3d3d; border-radius: 8px;">
                <div style="font-weight: 600; color: #999; margin-bottom: 6px;">Ticket Types</div>
                <div style="display: flex; flex-wrap: wrap; gap: 6px;">
                  ${Object.entries(ticketTypeBreakdown).map(
                    ([type, count]) => html`
                      <span
                        style="font-size: 11px; padding: 2px 8px; border-radius: 4px; background: ${getTicketTypeBadgeColor(
                          type,
                        )}; color: white;"
                        >${type}: ${count}</span
                      >
                    `,
                  )}
                </div>
              </div>`
            : ''}
          <div
            style="display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #3d3d3d; border-radius: 8px;"
          >
            <span style="font-weight: 600; color: #999;">Available Time</span>
            <span>${availableTime > 0 ? formatTravelTime(availableTime) : 'N/A'}</span>
          </div>
          <div
            style="display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #3d3d3d; border-radius: 8px;"
          >
            <span style="font-weight: 600; color: #999;">Assigned Time (travel + service)</span>
            <span>${assignedTime > 0 ? formatTravelTime(assignedTime) : 'N/A'}</span>
          </div>
          <div
            style="display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #3d3d3d; border-radius: 8px;"
          >
            <span style="font-weight: 600; color: #999;">Ticket Revenue</span>
            <span>${formatCurrency(profitability.ticketRevenue)}</span>
          </div>
          <div
            style="display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #3d3d3d; border-radius: 8px;"
          >
            <span style="font-weight: 600; color: #999;">FE Base Cost</span>
            <span>${formatCurrency(profitability.baseFeCost)}</span>
          </div>
          <div
            style="display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #3d3d3d; border-radius: 8px;"
          >
            <span style="font-weight: 600; color: #999;">FE Cost Factor</span>
            <span>${(profitability.feCostFactor * 100).toFixed(0)}%</span>
          </div>
          <div
            style="display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #3d3d3d; border-radius: 8px;"
          >
            <span style="font-weight: 600; color: #999;">Vehicle Cost</span>
            <span>${formatCurrency(profitability.vehicleCost)}</span>
          </div>
          <div
            style="display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #3d3d3d; border-radius: 8px;"
          >
            <span style="font-weight: 600; color: #999;">Overtime Cost</span>
            <span>${formatCurrency(profitability.overtimeCost)}</span>
          </div>
          <div
            style="display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #3d3d3d; border-radius: 8px;"
          >
            <span style="font-weight: 600; color: #999;">PUDO Cost</span>
            <span>${formatCurrency(profitability.pudoCost)}</span>
          </div>
          <div
            style="display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #3d3d3d; border-radius: 8px;"
          >
            <span style="font-weight: 600; color: #999;">Total Cost</span>
            <span>${formatCurrency(profitability.adjustedFeCost)}</span>
          </div>
          <div
            style="display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: #3d3d3d; border-radius: 8px; border: 1px solid ${profitability.profitDelta >=
            0
              ? '#4caf50'
              : '#e53935'};"
          >
            <span style="font-weight: 600; color: #999;">Profitability</span>
            <span style="color: ${profitability.profitDelta >= 0 ? '#4caf50' : '#e53935'}; font-weight: 600;"
              >${formatCurrency(profitability.profitDelta)}</span
            >
          </div>
        </div>
      </div>
    </div>
  `;
}

function groupRoutesByArea<T extends { vehicleId?: string }>(
  routes: T[],
): globalThis.Map<string, { route: T; originalIndex: number }[]> {
  const grouped = new globalThis.Map<string, { route: T; originalIndex: number }[]>();
  routes.forEach((route, idx) => {
    const area = getAreaFromVehicleId(route.vehicleId);
    if (!grouped.has(area)) {
      grouped.set(area, []);
    }
    grouped.get(area)!.push({ route, originalIndex: idx });
  });
  return grouped;
}

interface RoutePlanningOverview {
  _id?: string;
  createdAt?: Date | null;
  simulationType?: string;
}

const colors = {
  waypoint: {
    icon: 'navigation',
    borderColor: '#1e90ff',
    backgroundColor: '#fff',
    textColor: '#1e90ff',
  },
  preassigned: {
    icon: 'navigation',
    borderColor: '#ff9800',
    backgroundColor: '#ff9800',
    textColor: '#fff',
  },
  imac: {
    icon: 'navigation',
    borderColor: '#9c27b0',
    backgroundColor: '#9c27b0',
    textColor: '#fff',
  },
  employee: {
    icon: 'icon-[material-symbols--person]',
    borderColor: '#00bcd4',
    backgroundColor: '#00bcd4',
    textColor: '#fff',
  },
  depot: {
    icon: 'icon-[material-symbols--home]',
    borderColor: '#ff9800',
    backgroundColor: '#ff9800',
    textColor: '#fff',
  },
  ticket: {
    icon: 'icon-[material-symbols--sell]',
    borderColor: '#e53935',
    backgroundColor: '#e53935',
    textColor: '#fff',
  },
  pudo: {
    icon: 'icon-[material-symbols--home-pin]',
    borderColor: '#7e57c2',
    backgroundColor: '#7e57c2',
    textColor: '#fff',
  },
};

type RouteMarkerMeta = {
  label: string;
  labelBottom?: string;
  type?: string;
  ticketType?: string;
  isPreassigned?: boolean;
};

function getRouteStopMarkerColors(meta: RouteMarkerMeta) {
  if (meta.ticketType === 'IMAC') return colors.imac;
  if (meta.isPreassigned) return colors.preassigned;
  return colors.waypoint;
}

function buildRouteMarkers(
  stops:
    | {
        type?: string | null;
        ticketId?: string | null;
        ticketType?: string | null;
        address?: string | null;
        startTime?: string | null;
        travelDurationMinutes?: number | null;
        serviceDurationMinutes?: number | null;
        lat?: number | null;
        lng?: number | null;
      }[]
    | undefined,
  routeInfo: {
    vehicleName?: string | null;
    vehicleId?: string | null;
  },
  depotNameByTicketId: ReadonlyMap<string, string>,
  preassignedTicketIds: ReadonlySet<string> = new Set(),
): {
  markers: { lng: number; lat: number; popup: Renderable }[];
  markerMeta: RouteMarkerMeta[];
} {
  const markers: { lng: number; lat: number; popup: Renderable }[] = [];
  const markerMeta: RouteMarkerMeta[] = [];
  if (!stops?.length) return { markers, markerMeta };

  const dedupedStops = dedupeDepotStops(stops);
  if (dedupedStops.length === 0) return { markers, markerMeta };

  const firstStop = dedupedStops.at(0);
  const lastStop = dedupedStops.at(-1);
  const isSameStartEnd = !!firstStop && !!lastStop && firstStop.lng === lastStop.lng && firstStop.lat === lastStop.lat;

  dedupedStops.forEach((stop, index) => {
    if (!stop.lng || !stop.lat) return;
    if (isSameStartEnd && index === dedupedStops.length - 1) return;

    const label = isSameStartEnd && index === 0 ? '1' : String(index + 1);
    const labelBottom = isSameStartEnd && index === 0 ? String(dedupedStops.length) : undefined;

    const stopType = stop.type ?? 'waypoint';
    const isPreassigned = !!stop.ticketId && preassignedTicketIds.has(stop.ticketId);
    const popupTitle =
      stopType === 'ticket'
        ? stop.ticketType === 'IMAC'
          ? `IMAC Stop #${index + 1}`
          : isPreassigned
            ? `Preassigned Stop #${index + 1}`
            : `Ticket Stop #${index + 1}`
        : stopType === 'employee'
          ? `FE Stop #${index + 1}`
          : `Stop #${index + 1}`;
    const formattedStartTime = stop.startTime
      ? format(new Date(stop.startTime), 'dd.MM.yyyy, HH:mm', { locale: de })
      : undefined;
    const depotName = inferDepotNameFromStop(stop, depotNameByTicketId);
    const popup = renderMarkerPopupContent({
      title: popupTitle,
      rows: [
        { label: 'FE', value: routeInfo.vehicleName ?? routeInfo.vehicleId ?? 'Unknown' },
        { label: 'FE ID', value: routeInfo.vehicleId ?? undefined },
        { label: 'Type', value: stopType },
        { label: 'Depot', value: depotName },
        { label: 'Ticket ID', value: stop.ticketId ?? undefined },
        { label: 'Ticket Type', value: stop.ticketType ?? undefined },
        {
          label: 'Preassigned',
          value: stopType === 'ticket' ? (isPreassigned ? 'Yes' : 'No') : undefined,
        },
        { label: 'Address', value: stop.address ?? undefined },
        { label: 'Start', value: formattedStartTime },
        {
          label: 'Travel',
          value:
            stop.travelDurationMinutes !== null && stop.travelDurationMinutes !== undefined
              ? formatTravelTime(stop.travelDurationMinutes)
              : undefined,
        },
        {
          label: 'Service',
          value:
            stop.serviceDurationMinutes !== null && stop.serviceDurationMinutes !== undefined
              ? formatTravelTime(stop.serviceDurationMinutes)
              : undefined,
        },
      ],
    });

    markers.push({ lng: stop.lng, lat: stop.lat, popup });
    markerMeta.push({
      label,
      labelBottom,
      type: stop.type ?? undefined,
      ticketType: stop.ticketType ?? undefined,
      isPreassigned,
    });
  });

  return { markers, markerMeta };
}

export const routePlanningRouter = makeRouter({
  root: {
    path: '/',
    render: stateful(() => {
      const mapboxAccessToken = routeOptimizationConfig.get('MAPBOX_PUBLIC_KEY');

      const routePlanningsOverviewQuery = useQuery<RouteOptimizationResult[]>(
        () =>
          subscribeRoutePlannings(50)({
            _id: 1,
            createdAt: 1,
            simulationType: 1,
          }),
        [],
      );

      const [selectedPlanningRunId, setSelectedPlanningRunId] = useState<string | undefined>(undefined);
      const [selectedRoute, setSelectedRoute] = useState<number | null>(null);
      const [expandedRoute, setExpandedRoute] = useState<number | null>(null);
      const [mapInitialized, setMapInitialized] = useState(false);
      const [showUnassignedDialog, setShowUnassignedDialog] = useState(false);
      const [showFourHourSlaDialog, setShowFourHourSlaDialog] = useState(false);
      const [showFieldEngineersWithoutRoutesDialog, setShowFieldEngineersWithoutRoutesDialog] = useState(false);
      const [showUnprofitableFieldEngineersDialog, setShowUnprofitableFieldEngineersDialog] = useState(false);
      const [showPudoMarkers, setShowPudoMarkers] = useState(true);

      const [routeClickRef] = useState<{ current: (idx: number) => void }>({ current: () => {} });

      const scrollToRoute = (idx: number) => {
        requestAnimationFrame(() => {
          const sidebar = document.querySelector('[data-sidebar-container]');
          const card = sidebar?.querySelector(`[data-route-idx="${idx}"]`);
          card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      };

      routeClickRef.current = (idx: number) => {
        if (selectedRoute === idx) {
          setSelectedRoute(null);
          setExpandedRoute(null);
        } else {
          setSelectedRoute(idx);
          setExpandedRoute(idx);
          scrollToRoute(idx);
        }
      };

      const { width: sidebarWidth, handleDirective: sidebarHandleDirective } = useHorizontalDragResize({
        initialWidth: 500,
        minWidth: 200,
        maxWidth: 600,
      });
      const [profitabilityBreakdown, setProfitabilityBreakdown] = useState<{
        name: string;
        id?: string | null;
        profitability: ReturnType<typeof getEngineerProfitabilitySummary>;
        ticketTypeBreakdown?: Record<string, number>;
        availableTimeMinutes?: number | null;
        isTotal?: boolean;
      } | null>(null);

      const routePlanningsOverview: RoutePlanningOverview[] = (routePlanningsOverviewQuery.value ?? []).map(
        planning => ({
          _id: planning._id ?? undefined,
          createdAt: planning.createdAt ?? undefined,
          simulationType: planning.simulationType ?? undefined,
        }),
      );
      const selectedPlanningId = (() => {
        if (routePlanningsOverview.length === 0) return undefined;
        if (selectedPlanningRunId && routePlanningsOverview.some(planning => planning._id === selectedPlanningRunId)) {
          return selectedPlanningRunId;
        }
        return routePlanningsOverview[0]?._id;
      })();
      const selectedPlanningOverview = routePlanningsOverview.find(planning => planning._id === selectedPlanningId);

      const selectedPlanningQuery = useQuery<RouteOptimizationResult | undefined>(
        () =>
          selectedPlanningId
            ? subscribeRoutePlanningById(selectedPlanningId)(selectionSet(() => RouteOptimizationResult, 3))
            : Promise.resolve(undefined),
        [selectedPlanningId],
      );
      const selectedPlanning = selectedPlanningQuery.value;

      const pudoLocationsQuery = useQuery<PudoLocation[]>(
        () =>
          showPudoMarkers && selectedPlanningId
            ? subscribePlanningPudoLocations(selectedPlanningId)({
                name: 1,
                lat: 1,
                lng: 1,
                ticketCount: 1,
              })
            : Promise.resolve([]),
        [selectedPlanningId, showPudoMarkers],
      );

      const unassignedTickets = selectedPlanning?.unscheduledTickets ?? [];
      const fourHourSlaTickets = selectedPlanning?.fourHourSlaTickets ?? [];

      const plannedEngineerKeys = new Set(
        (selectedPlanning?.routes ?? [])
          .flatMap(r => [r.vehicleId, r.vehicleName])
          .filter(Boolean)
          .map(v => String(v).trim().toLowerCase()),
      );

      const engineerKeysWithExternalTickets = new Set(
        [
          ...(selectedPlanning?.ticketsWithFETomorrow ?? [])
            .filter(ticket => ticket.type === 'IMAC')
            .map(ticket => normalizeLookupKey(ticket.employeeId)),
          ...(selectedPlanning?.fixedFETickets ?? []).map(ticket => normalizeLookupKey(ticket.employeeId)),
        ].filter((key): key is string => Boolean(key)),
      );

      const externalTicketEngineersByKey = new Map<
        string,
        {
          id?: string | null;
          name?: string | null;
          startTime?: string | Date | null;
          endTime?: string | Date | null;
        }
      >();
      for (const fe of selectedPlanning?.fieldEngineersAvailable ?? []) {
        const idKey = normalizeLookupKey(fe.id);
        const nameKey = normalizeLookupKey(fe.name);
        const hasPlannedRoute =
          (idKey && plannedEngineerKeys.has(idKey)) || (nameKey && plannedEngineerKeys.has(nameKey));
        const hasExternalTickets =
          (idKey && engineerKeysWithExternalTickets.has(idKey)) ||
          (nameKey && engineerKeysWithExternalTickets.has(nameKey));
        if (hasPlannedRoute || !hasExternalTickets) continue;
        if (idKey && !externalTicketEngineersByKey.has(idKey)) {
          externalTicketEngineersByKey.set(idKey, fe);
        }
        if (nameKey && !externalTicketEngineersByKey.has(nameKey)) {
          externalTicketEngineersByKey.set(nameKey, fe);
        }
      }
      for (const ticket of [
        ...(selectedPlanning?.ticketsWithFETomorrow ?? []) /*.filter(ticket => ticket.type === 'IMAC')*/,
        ...(selectedPlanning?.fixedFETickets ?? []),
      ]) {
        if (
          selectedPlanning?.routes?.some(route =>
            route.stops?.some(stop => stop.type === 'ticket' && stop.ticketId === ticket.id),
          )
        )
          continue;
        const employeeId = typeof ticket.employeeId === 'string' ? ticket.employeeId.trim() : '';
        const employeeKey = normalizeLookupKey(employeeId);
        if (!employeeKey || plannedEngineerKeys.has(employeeKey) || externalTicketEngineersByKey.has(employeeKey)) {
          continue;
        }
        externalTicketEngineersByKey.set(employeeKey, {
          id: employeeId || undefined,
          name: employeeId || undefined,
          startTime: ticket.startTime,
          endTime: ticket.endTime,
        });
      }
      const fieldEngineersWithExternalTickets = Array.from(new Set(externalTicketEngineersByKey.values())).sort(
        (a, b) => `${a.name ?? a.id ?? ''}`.localeCompare(`${b.name ?? b.id ?? ''}`),
      );

      const fieldEngineersWithoutRoutes = getFieldEngineersWithoutRoutes(selectedPlanning);
      const fieldEngineersWithoutRoutesWithCoords = getFieldEngineersWithoutRoutesWithCoords(selectedPlanning);

      const fieldEngineerScheduleByKey = new globalThis.Map<
        string,
        {
          startTime?: string | Date | null;
          endTime?: string | Date | null;
          theoreticalStartTime?: string | Date | null;
          theoreticalEndTime?: string | Date | null;
        }
      >();
      for (const fe of selectedPlanning?.fieldEngineersAvailable ?? []) {
        const keys = [fe.id, fe.name].filter(Boolean).map(value => String(value).trim().toLowerCase());
        for (const key of keys) {
          if (!fieldEngineerScheduleByKey.has(key)) {
            fieldEngineerScheduleByKey.set(key, {
              startTime: fe.startTime,
              endTime: fe.endTime,
              theoreticalStartTime: fe.theoreticalStartTime,
              theoreticalEndTime: fe.theoreticalEndTime,
            });
          }
        }
      }

      const fieldEngineerLabelById = new Map<string, string>();
      for (const fe of selectedPlanning?.fieldEngineersAvailable ?? []) {
        const id = fe.id?.trim();
        if (!id) continue;
        const name = fe.name?.trim();
        fieldEngineerLabelById.set(id.toLowerCase(), name ? `${name} (${id})` : id);
      }

      const routeRevenueByEngineer = getRouteRevenueByEngineer(selectedPlanning);

      // fixed fe tickets
      for (const fixedFETicket of selectedPlanning?.fixedFETickets ?? []) {
        const engineerKey = fixedFETicket.employeeId?.trim().toLowerCase();
        if (!engineerKey) continue;

        // we assume fixed fe tickets are never imac tickets, because we handle imac tickets extra
        if (fixedFETicket.type === 'IMAC') continue;

        // most tickets should be planned in routes, even fixedFETickets
        if (
          selectedPlanning?.routes?.some(r =>
            r.stops?.some(s => s.type === 'ticket' && s.ticketId === fixedFETicket.id),
          )
        ) {
          continue;
        }

        const ticketRevenue = getTicketRevenue(fixedFETicket.type);
        const area = getAreaFromVehicleId(engineerKey);
        const existingRouteRevenue = routeRevenueByEngineer.get(engineerKey) ?? {
          ticketCount: 0,
          ticketRevenue: 0,
          area,
          travelDistanceKm: 0,
        };
        existingRouteRevenue.ticketCount++;
        existingRouteRevenue.ticketRevenue += ticketRevenue;

        routeRevenueByEngineer.set(engineerKey, existingRouteRevenue);
      }

      // imac tickets
      for (const imacTicket of (selectedPlanning?.ticketsWithFETomorrow ?? []).filter(t => t.type === 'IMAC')) {
        const engineerKey = imacTicket.employeeId?.trim().toLowerCase();
        if (!engineerKey) continue;

        // most tickets should be planned in routes, even imac tickets
        if (
          selectedPlanning?.routes?.some(r => r.stops?.some(s => s.type === 'ticket' && s.ticketId === imacTicket.id))
        ) {
          continue;
        }

        // ? do we want to leave this at half-day / full-day or as granular as this is now
        const percentileOfFullDay =
          imacTicket.endTime && imacTicket.startTime
            ? Math.min(
                VEHICLE_MAX_ROUTE_DURATION_MINUTES,
                differenceInMinutes(imacTicket.endTime, imacTicket.startTime),
              ) / VEHICLE_MAX_ROUTE_DURATION_MINUTES
            : 1;

        const ticketRevenue = getTicketRevenue(imacTicket.type) * percentileOfFullDay;
        const area = getAreaFromVehicleId(engineerKey);
        const existingRouteRevenue = routeRevenueByEngineer.get(engineerKey) ?? {
          ticketCount: 0,
          ticketRevenue: 0,
          area,
          travelDistanceKm: 0,
        };
        existingRouteRevenue.ticketCount++;
        existingRouteRevenue.ticketRevenue += ticketRevenue;

        routeRevenueByEngineer.set(engineerKey, existingRouteRevenue);
      }

      const unprofitableFieldEngineers = (selectedPlanning?.fieldEngineersAvailable ?? [])
        .map(fe => {
          const profitability = getEngineerProfitabilitySummary(fe, routeRevenueByEngineer, selectedPlanning);

          return {
            name: fe.name ?? fe.id ?? 'N/A',
            id: fe.id,
            postalCode: fe.postalCode,
            country: fe.country,
            startTime: fe.startTime,
            endTime: fe.endTime,
            ticketCount: profitability.ticketCount,
            ticketRevenue: profitability.ticketRevenue,
            area: profitability.area,
            travelDistanceKm: profitability.travelDistanceKm,
            travelCost: profitability.travelDistanceKm * VEHICLE_COST_PER_KILOMETER,
            adjustedFeCost: profitability.adjustedFeCost,
            profitDelta: profitability.profitDelta,
          };
        })
        .filter(fe => fe.ticketCount > 0 && fe.ticketRevenue < fe.adjustedFeCost)
        .sort((a, b) => a.name.localeCompare(b.name));

      const depotNameByTicketId = new globalThis.Map<string, string>();
      for (const ticket of [
        ...(selectedPlanning?.ticketsToSchedule ?? []),
        ...(selectedPlanning?.ticketsWithFETomorrow ?? []),
        ...(selectedPlanning?.unscheduledTickets ?? []),
      ]) {
        if (!ticket.id || !ticket.pudoName) continue;
        if (!depotNameByTicketId.has(ticket.id)) {
          depotNameByTicketId.set(ticket.id, ticket.pudoName);
        }
      }

      const preassignedTicketIds = new Set<string>();
      for (const ticket of selectedPlanning?.ticketsWithFETomorrow ?? []) {
        if (ticket.id) preassignedTicketIds.add(ticket.id);
      }
      for (const ticket of selectedPlanning?.fixedFETickets ?? []) {
        if (ticket.id) preassignedTicketIds.add(ticket.id);
      }
      for (const ticket of selectedPlanning?.ticketsToSchedule ?? []) {
        if (ticket.id && 'fixedFE' in ticket && ticket.fixedFE === true) {
          preassignedTicketIds.add(ticket.id);
        }
      }

      const mapRoutes = (selectedPlanning?.routes ?? []).map(route => {
        const { markers, markerMeta } = buildRouteMarkers(
          route.stops,
          {
            vehicleName: route.vehicleName,
            vehicleId: route.vehicleId,
          },
          depotNameByTicketId,
          preassignedTicketIds,
        );
        return {
          markers,
          color: route.color ?? colors.waypoint.borderColor,
          lineWidth: 5,
          template: (index: number) => {
            const meta = markerMeta[index];
            if (!meta) return html``;
            const markerColors = getRouteStopMarkerColors(meta);
            return html`
              <x-marker
                .label=${meta.label}
                .labelBottom=${meta.labelBottom}
                .textColor=${markerColors.textColor}
                .backgroundColor=${markerColors.backgroundColor}
                .borderColor=${markerColors.borderColor}
              ></x-marker>
            `;
          },
        };
      });

      const unassignedMarkers = unassignedTickets.flatMap(ticket => {
        if (!ticket.lng || !ticket.lat) return [];
        return [
          {
            lng: ticket.lng,
            lat: ticket.lat,
            popup: renderMarkerPopupContent({
              title: `Unassigned Ticket ${ticket.number ?? ticket.id ?? ''}`.trim(),
              rows: [
                { label: 'Ticket ID', value: ticket.id ?? undefined },
                { label: 'Ticket Number', value: ticket.number ?? undefined },
                { label: 'Type', value: ticket.type ?? undefined },
                {
                  label: 'Working Time',
                  value: formatTravelTime(getTicketDurationMinutes(ticket)),
                },
                {
                  label: 'Address',
                  value:
                    `${ticket.address ?? ''}${ticket.zip || ticket.city ? `, ${ticket.zip ?? ''} ${ticket.city ?? ''}` : ''}`.trim() ||
                    undefined,
                },
                { label: 'PUDO', value: ticket.pudoName ?? undefined },
                {
                  label: 'Allowed FEs',
                  value: ticket.assignableEmployeeIds
                    ? ticket.assignableEmployeeIds.filter(Boolean).join(', ') || 'none'
                    : 'all',
                },
                {
                  label: 'Was Assigned To',
                  value: ticket.wasAssignedTo
                    ? (fieldEngineerLabelById.get(ticket.wasAssignedTo.toLowerCase()) ?? ticket.wasAssignedTo)
                    : undefined,
                },
              ],
            }) as Renderable,
          },
        ];
      });

      const unassignedMarkerGroup = unassignedMarkers.length
        ? [
            {
              markers: unassignedMarkers,
              clusterOptions: { enabled: false },
              template: html`
                <x-marker
                  .icon=${colors.ticket.icon}
                  .textColor=${colors.ticket.textColor}
                  .backgroundColor=${colors.ticket.backgroundColor}
                  .borderColor=${colors.ticket.borderColor}
                ></x-marker>
              `,
            },
          ]
        : undefined;

      const feWithoutRoutesMarkers = fieldEngineersWithoutRoutesWithCoords.flatMap(fe => {
        if (!fe.lng || !fe.lat) return [];
        return [
          {
            lng: fe.lng,
            lat: fe.lat,
            popup: renderMarkerPopupContent({
              title: `FE without route: ${fe.name ?? fe.id ?? 'Unknown'}`,
              rows: [
                { label: 'Name', value: fe.name ?? undefined },
                { label: 'ID', value: fe.id ?? undefined },
                {
                  label: 'Location',
                  value: `${fe.postalCode ?? ''}${fe.country ? `, ${fe.country}` : ''}`.trim() || undefined,
                },
                {
                  label: 'Start',
                  value: fe.startTime ? format(new Date(fe.startTime), 'dd.MM.yyyy, HH:mm', { locale: de }) : undefined,
                },
                {
                  label: 'End',
                  value: fe.endTime ? format(new Date(fe.endTime), 'dd.MM.yyyy, HH:mm', { locale: de }) : undefined,
                },
              ],
            }) as Renderable,
          },
        ];
      });

      const feWithoutRoutesMarkerGroup = feWithoutRoutesMarkers.length
        ? [
            {
              markers: feWithoutRoutesMarkers,
              clusterOptions: { enabled: false },
              template: html`
                <x-marker
                  .icon=${colors.employee.icon}
                  .textColor=${colors.employee.textColor}
                  .backgroundColor=${colors.employee.backgroundColor}
                  .borderColor=${colors.employee.borderColor}
                ></x-marker>
              `,
            },
          ]
        : undefined;

      const pudoMarkers = (pudoLocationsQuery.value ?? []).flatMap(pudo => {
        if (!Number.isFinite(pudo.lng) || !Number.isFinite(pudo.lat)) return [];
        return [
          {
            lng: pudo.lng,
            lat: pudo.lat,
            popup: renderMarkerPopupContent({
              title: pudo.name,
              rows: [
                { label: 'PUDO', value: pudo.name },
                { label: 'Tickets', value: pudo.ticketCount },
              ],
            }) as Renderable,
          },
        ];
      });

      const pudoMarkerGroup =
        showPudoMarkers && pudoMarkers.length
          ? [
              {
                markers: pudoMarkers,
                clusterOptions: { enabled: true, maxZoom: 10 },
                template: html`
                  <x-marker
                    .icon=${colors.pudo.icon}
                    .textColor=${colors.pudo.textColor}
                    .backgroundColor=${colors.pudo.backgroundColor}
                    .borderColor=${colors.pudo.borderColor}
                  ></x-marker>
                `,
              },
            ]
          : undefined;

      const allMarkerGroups = [
        ...(unassignedMarkerGroup ?? []),
        ...(feWithoutRoutesMarkerGroup ?? []),
        ...(pudoMarkerGroup ?? []),
      ];

      return html`
        <!-- Unassigned Tickets Dialog -->
        ${showUnassignedDialog
          ? html`<div
              style="position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1001;"
              @click=${(e: Event) => {
                if (e.target === e.currentTarget) setShowUnassignedDialog(false);
              }}
            >
              <div
                style="background: #2d2d2d; border-radius: 12px; padding: 24px; max-width: 600px; max-height: 80vh; overflow-y: auto; color: white; min-width: 400px;"
              >
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
                  <h3 style="margin: 0; font-size: 18px; display: flex; align-items: center; gap: 8px;">
                    <span class="icon-[material-symbols--warning]" style="color: #ff9800;"></span>
                    Unassigned Tickets (${unassignedTickets?.length ?? 0})
                  </h3>
                  <button
                    style="background: transparent; border: none; color: #888; cursor: pointer; padding: 4px;"
                    @click=${() => setShowUnassignedDialog(false)}
                  >
                    <span class="icon-[material-symbols--close]"></span>
                  </button>
                </div>
                <div style="display: flex; flex-direction: column; gap: 8px;">
                  ${unassignedTickets.map(
                    ticket => html`
                      <div
                        style="background: #3d3d3d; border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 4px;"
                      >
                        <div style="display: flex; justify-content: space-between; align-items: center;">
                          <div style="display: flex; align-items: center; gap: 8px;">
                            <span style="font-weight: 600; color: #fff;">${ticket.number ?? ticket.id ?? 'N/A'}</span>
                            ${ticket.id
                              ? html`
                                  <a
                                    href=${`${ticketsBaseUrl}/otrs/index.pl?Action=AgentTicketZoom;TicketID=${encodeURIComponent(
                                      String(ticket.id),
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style="color: #2196f3; text-decoration: none; font-size: 11px;"
                                    >Open ticket</a
                                  >
                                `
                              : ''}
                          </div>
                          <span
                            style="font-size: 11px; padding: 2px 8px; border-radius: 4px; background: ${ticket.type ===
                            'Client'
                              ? '#2196f3'
                              : ticket.type === 'Enterprise'
                                ? '#9c27b0'
                                : ticket.type === 'HES'
                                  ? '#4caf50'
                                  : '#ff9800'}; color: white;"
                            >${ticket.type ?? 'Unknown'}</span
                          >
                        </div>
                        <div style="font-size: 12px; color: #aaa;">
                          ${ticket.address ?? ''}${ticket.zip || ticket.city
                            ? `, ${ticket.zip ?? ''} ${ticket.city ?? ''}`
                            : ''}
                        </div>
                        <div style="font-size: 11px; color: #888; display: flex; gap: 8px; align-items: center;">
                          <span style="font-weight: 600; color: #999;">PUDO</span>
                          ${ticket.pudoName
                            ? (() => {
                                const address = `${ticket.pudoName}`.trim();
                                return html`
                                  <span>${address}</span>
                                  <a
                                    href=${`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                      address,
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style="color: #2196f3; text-decoration: none; font-size: 11px;"
                                    >Open</a
                                  >
                                `;
                              })()
                            : html`<span>N/A</span>`}
                        </div>
                        <div style="font-size: 11px; color: #888; display: flex; gap: 8px; align-items: center;">
                          <span style="font-weight: 600; color: #999;">Fixed FE</span>
                          <span>${formatFixedFEStatus(ticket)}</span>
                        </div>
                        ${formatFixedFEStatus(ticket) === 'Yes'
                          ? html`<div
                              style="font-size: 11px; color: #888; display: flex; gap: 8px; align-items: center;"
                            >
                              <span style="font-weight: 600; color: #999;">Expected FE</span>
                              <span>${getExpectedFELabel(ticket, fieldEngineerLabelById)}</span>
                            </div>`
                          : ''}
                        ${selectedPlanning?.simulationType === 'checksum' &&
                        (ticket.wasAssignedTo || (ticket as RouteTicketAssigned).employeeId)
                          ? html`<div
                              style="font-size: 11px; color: #888; display: flex; gap: 8px; align-items: center;"
                            >
                              <span style="font-weight: 600; color: #999;">Was Assigned To</span>
                              <span
                                >${(ticket.wasAssignedTo || (ticket as RouteTicketAssigned).employeeId) ??
                                ticket.wasAssignedTo}
                              </span>
                            </div>`
                          : ''}
                        ${ticket.comment
                          ? html`<div
                              style="font-size: 11px; color: #888; display: flex; gap: 8px; align-items: flex-start;"
                            >
                              <span style="font-weight: 600; color: #999; flex-shrink: 0;">Comment</span>
                              <span>${ticket.comment}</span>
                            </div>`
                          : ''}
                      </div>
                    `,
                  )}
                </div>
              </div>
            </div>`
          : ''}

        <!-- Unprofitable Field Engineers Dialog -->
        ${showUnprofitableFieldEngineersDialog
          ? html`<div
              style="position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1001;"
              @click=${(e: Event) => {
                if (e.target === e.currentTarget) setShowUnprofitableFieldEngineersDialog(false);
              }}
            >
              <div
                style="background: #2d2d2d; border-radius: 12px; padding: 24px; max-width: 600px; max-height: 80vh; overflow-y: auto; color: white; min-width: 400px;"
              >
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
                  <h3 style="margin: 0; font-size: 18px; display: flex; align-items: center; gap: 8px;">
                    <span class="icon-[material-symbols--trending-down]" style="color: #ff9800;"></span>
                    Unprofitable Field Engineers (${unprofitableFieldEngineers.length})
                  </h3>
                  <button
                    style="background: transparent; border: none; color: #888; cursor: pointer; padding: 4px;"
                    @click=${() => setShowUnprofitableFieldEngineersDialog(false)}
                  >
                    <span class="icon-[material-symbols--close]"></span>
                  </button>
                </div>
                <div style="display: flex; flex-direction: column; gap: 8px;">
                  ${unprofitableFieldEngineers.map(
                    fe => html`
                      <div
                        style="background: #3d3d3d; border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 4px;"
                      >
                        <div style="display: flex; justify-content: space-between; align-items: center;">
                          <div style="display: flex; align-items: center; gap: 8px;">
                            <span style="font-weight: 600; color: #fff;">${fe.name}</span>
                            ${fe.id && fe.id !== fe.name
                              ? html`<span style="font-size: 11px; color: #999;">${fe.id}</span>`
                              : ''}
                          </div>
                          <span
                            style="font-size: 11px; padding: 2px 8px; border-radius: 4px; background: #ff9800; color: white;"
                            >Unprofitable</span
                          >
                        </div>
                        <div style="font-size: 12px; color: #aaa;">
                          ${fe.postalCode ?? ''}${fe.country ? `, ${fe.country}` : ''}
                        </div>
                        ${fe.startTime || fe.endTime
                          ? html`<div style="font-size: 11px; color: #888; display: flex; gap: 12px; flex-wrap: wrap;">
                              ${fe.startTime
                                ? html`<span>
                                    <span style="font-weight: 600; color: #999;">Start</span>
                                    ${format(new Date(fe.startTime), 'dd.MM.yyyy, HH:mm', { locale: de })}
                                  </span>`
                                : ''}
                              ${fe.endTime
                                ? html`<span>
                                    <span style="font-weight: 600; color: #999;">End</span>
                                    ${format(new Date(fe.endTime), 'dd.MM.yyyy, HH:mm', { locale: de })}
                                  </span>`
                                : ''}
                            </div>`
                          : ''}
                        <div style="font-size: 11px; color: #888; display: flex; gap: 12px; flex-wrap: wrap;">
                          <span><span style="font-weight: 600; color: #999;">Area</span> ${fe.area}</span>
                          <span><span style="font-weight: 600; color: #999;">Tickets</span> ${fe.ticketCount}</span>
                          <span
                            ><span style="font-weight: 600; color: #999;">Travel</span> ${formatDistanceKm(
                              fe.travelDistanceKm,
                            )}</span
                          >
                          <span
                            ><span style="font-weight: 600; color: #999;">Travel cost</span> ${formatCurrency(
                              fe.travelCost,
                            )}</span
                          >
                          <span
                            ><span style="font-weight: 600; color: #999;">Revenue</span> ${formatCurrency(
                              fe.ticketRevenue,
                            )}</span
                          >
                          <span
                            ><span style="font-weight: 600; color: #999;">Gap</span> ${formatCurrency(
                              fe.profitDelta,
                            )}</span
                          >
                        </div>
                      </div>
                    `,
                  )}
                </div>
              </div>
            </div>`
          : ''}

        <!-- Profitability Breakdown Dialog -->
        ${profitabilityBreakdown
          ? renderProfitabilityBreakdownDialog(profitabilityBreakdown, () => setProfitabilityBreakdown(null))
          : ''}

        <!-- 4h SLA Tickets Dialog -->
        ${showFourHourSlaDialog
          ? html`<div
              style="position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1001;"
              @click=${(e: Event) => {
                if (e.target === e.currentTarget) setShowFourHourSlaDialog(false);
              }}
            >
              <div
                style="background: #2d2d2d; border-radius: 12px; padding: 24px; max-width: 600px; max-height: 80vh; overflow-y: auto; color: white; min-width: 400px;"
              >
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
                  <h3 style="margin: 0; font-size: 18px; display: flex; align-items: center; gap: 8px;">
                    <span class="icon-[material-symbols--timer]" style="color: #ff9800;"></span>
                    4h SLA Tickets (${fourHourSlaTickets?.length ?? 0})
                  </h3>
                  <button
                    style="background: transparent; border: none; color: #888; cursor: pointer; padding: 4px;"
                    @click=${() => setShowFourHourSlaDialog(false)}
                  >
                    <span class="icon-[material-symbols--close]"></span>
                  </button>
                </div>
                <div style="display: flex; flex-direction: column; gap: 8px;">
                  ${fourHourSlaTickets.map(
                    ticket => html`
                      <div
                        style="background: #3d3d3d; border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 4px;"
                      >
                        <div style="display: flex; justify-content: space-between; align-items: center;">
                          <div style="display: flex; align-items: center; gap: 8px;">
                            <span style="font-weight: 600; color: #fff;">${ticket.number ?? ticket.id ?? 'N/A'}</span>
                            ${ticket.id
                              ? html`
                                  <a
                                    href=${`${ticketsBaseUrl}/otrs/index.pl?Action=AgentTicketZoom;TicketID=${encodeURIComponent(
                                      String(ticket.id),
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style="color: #2196f3; text-decoration: none; font-size: 11px;"
                                    >Open ticket</a
                                  >
                                `
                              : ''}
                          </div>
                          <span
                            style="font-size: 11px; padding: 2px 8px; border-radius: 4px; background: ${ticket.type ===
                            'Client'
                              ? '#2196f3'
                              : ticket.type === 'Enterprise'
                                ? '#9c27b0'
                                : ticket.type === 'HES'
                                  ? '#4caf50'
                                  : '#ff9800'}; color: white;"
                            >${ticket.type ?? 'Unknown'}</span
                          >
                        </div>
                        <div style="font-size: 12px; color: #aaa;">
                          ${ticket.address ?? ''}${ticket.zip || ticket.city
                            ? `, ${ticket.zip ?? ''} ${ticket.city ?? ''}`
                            : ''}
                        </div>
                        <div style="font-size: 11px; color: #888; display: flex; gap: 8px; align-items: center;">
                          <span style="font-weight: 600; color: #999;">PUDO</span>
                          ${ticket.pudoName
                            ? (() => {
                                const address = `${ticket.pudoName}`.trim();
                                return html`
                                  <span>${address}</span>
                                  <a
                                    href=${`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                      address,
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style="color: #2196f3; text-decoration: none; font-size: 11px;"
                                    >Open</a
                                  >
                                `;
                              })()
                            : html`<span>N/A</span>`}
                        </div>
                      </div>
                    `,
                  )}
                </div>
              </div>
            </div>`
          : ''}

        <!-- Field Engineers Without Routes Dialog -->
        ${showFieldEngineersWithoutRoutesDialog
          ? html`<div
              style="position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1001;"
              @click=${(e: Event) => {
                if (e.target === e.currentTarget) setShowFieldEngineersWithoutRoutesDialog(false);
              }}
            >
              <div
                style="background: #2d2d2d; border-radius: 12px; padding: 24px; max-width: 600px; max-height: 80vh; overflow-y: auto; color: white; min-width: 400px;"
              >
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
                  <h3 style="margin: 0; font-size: 18px; display: flex; align-items: center; gap: 8px;">
                    <span class="icon-[material-symbols--person-off]" style="color: #ff9800;"></span>
                    Field Engineers without routes (${fieldEngineersWithoutRoutes?.length ?? 0})
                  </h3>
                  <button
                    style="background: transparent; border: none; color: #888; cursor: pointer; padding: 4px;"
                    @click=${() => setShowFieldEngineersWithoutRoutesDialog(false)}
                  >
                    <span class="icon-[material-symbols--close]"></span>
                  </button>
                </div>
                <div style="display: flex; flex-direction: column; gap: 8px;">
                  ${(fieldEngineersWithoutRoutes ?? []).map(
                    fe => html`
                      <div
                        style="background: #3d3d3d; border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 4px;"
                      >
                        <div style="display: flex; justify-content: space-between; align-items: center;">
                          <div style="display: flex; align-items: center; gap: 8px;">
                            <span style="font-weight: 600; color: #fff;">${fe.name ?? fe.id ?? 'N/A'}</span>
                            ${fe.id && fe.name && fe.id !== fe.name
                              ? html`<span style="font-size: 11px; color: #999;">${fe.id}</span>`
                              : ''}
                          </div>
                          <span
                            style="font-size: 11px; padding: 2px 8px; border-radius: 4px; background: #ff9800; color: white;"
                            >Available</span
                          >
                        </div>
                        <div style="font-size: 12px; color: #aaa;">
                          ${fe.postalCode ?? ''}${fe.country ? `, ${fe.country}` : ''}
                        </div>
                        ${fe.startTime || fe.endTime
                          ? html`<div style="font-size: 11px; color: #888; display: flex; gap: 12px; flex-wrap: wrap;">
                              ${fe.startTime
                                ? html`<span>
                                    <span style="font-weight: 600; color: #999;">Start</span>
                                    ${format(new Date(fe.startTime), 'dd.MM.yyyy, HH:mm', { locale: de })}
                                  </span>`
                                : ''}
                              ${fe.endTime
                                ? html`<span>
                                    <span style="font-weight: 600; color: #999;">End</span>
                                    ${format(new Date(fe.endTime), 'dd.MM.yyyy, HH:mm', { locale: de })}
                                  </span>`
                                : ''}
                            </div>`
                          : ''}
                      </div>
                    `,
                  )}
                </div>
              </div>
            </div>`
          : ''}

        <div style="display: flex;" class="flex-1 min-h-0">
          <!-- Sidebar -->
          <div
            data-sidebar-container
            style="width: ${sidebarWidth}px; background: #1e1e1e; color: white; overflow-y: auto; padding: 16px; flex-shrink: 0;"
          >
            <div style="margin-bottom: 16px;">
              <h3 style="margin: 0 0 4px 0; font-size: 16px; font-weight: 600;">Routes</h3>
              ${routePlanningsOverview.length > 1
                ? html`<div style="margin-bottom: 8px;">
                    <div
                      class="[&_.input]:bg-base-200 [&_.input]:text-base-content [&_.dropdown]:bg-base-200 [&_.dropdown]:text-base-content [&_input::placeholder]:text-base-content/60"
                    >
                      ${DropdownSelectionInput<RoutePlanningOverview>({
                        placeholder: 'Select planning run',
                        placeholderMode: 'hide',
                        selectables: routePlanningsOverview,
                        value: selectedPlanningOverview ?? null,
                        clearable: false,
                        resolveID: planning => planning._id ?? '',
                        renderString: planning => {
                          const timestamp = planning.createdAt
                            ? format(planning.createdAt, 'dd.MM.yyyy, HH:mm', { locale: de })
                            : 'Unknown date';
                          const simulationLabel = planning.simulationType ? ` (${planning.simulationType})` : '';
                          const isLatest = planning._id === routePlanningsOverview[0]?._id;
                          return `${isLatest ? 'Latest: ' : ''}${timestamp}${simulationLabel}`;
                        },
                        onChanged: value => {
                          setSelectedPlanningRunId(value?._id ?? undefined);
                          setSelectedRoute(null);
                          setExpandedRoute(null);
                        },
                      })}
                    </div>
                  </div>`
                : ''}
              ${selectedPlanning?.createdAt
                ? html`<div style="font-size: 11px; color: #888;">
                    Selected Run: ${format(selectedPlanning.createdAt, 'dd.MM.yyyy, HH:mm', { locale: de })}
                  </div>`
                : ''}
              <label
                style="display: flex; align-items: center; gap: 8px; margin-top: 10px; font-size: 12px; color: #bbb; cursor: pointer;"
              >
                <input
                  type="checkbox"
                  .checked=${showPudoMarkers}
                  @change=${(event: Event) => {
                    const checked = (event.currentTarget as HTMLInputElement).checked;
                    setShowPudoMarkers(checked);
                  }}
                />
                <span>Show PUDO markers</span>
              </label>
              ${showPudoMarkers && pudoLocationsQuery.loading
                ? html`<div style="font-size: 11px; color: #888; margin-top: 4px;">Loading PUDO markers...</div>`
                : ''}
              ${showPudoMarkers && pudoLocationsQuery.error
                ? html`<div style="font-size: 11px; color: #e53935; margin-top: 4px;">
                    Failed to load PUDO markers: ${pudoLocationsQuery.error.message}
                  </div>`
                : ''}
            </div>

            <!-- Statistics Section -->
            ${selectedPlanning && selectedPlanning.routes.length > 0
              ? (() => {
                  const stats = calculateStats(selectedPlanning);
                  const freeWorkingTime = getFreeAvailableWorkingTimeMinutes(selectedPlanning);
                  const totalTravelKilometers = stats.totalTravelMeters / 1000;
                  const traveledKilometersPerFE =
                    stats.totalFesWithRoutes > 0 ? totalTravelKilometers / stats.totalFesWithRoutes : 0;
                  const accumulatedKilometersPerTicket =
                    stats.totalTickets > 0 ? totalTravelKilometers / stats.totalTickets : 0;

                  // add up all planned fes (all planned fes have routes, because we calculate routes for all fes with tickets) + subtract day cost for each available but unplanned FE
                  const totalProfitabilitySummary = getTotalProfitabilitySummary(selectedPlanning);
                  const totalProfitability = totalProfitabilitySummary.profitDelta;

                  return html`
                    <div
                      style="background: #2d2d2d; border-radius: 8px; padding: 12px; margin-bottom: 16px; font-size: 12px;"
                    >
                      <div
                        style="font-size: 11px; font-weight: 600; color: #888; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;"
                      >
                        Statistics
                      </div>

                      <!-- Global Stats -->
                      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px;">
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Available internal Field Engineers"
                        >
                          <span class="icon-[material-symbols--person]" style="font-size: 16px; color: #00bcd4;"></span>
                          <span><strong>${stats.totalAvailableInternalFEs}</strong> available internal FEs</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Assigned internal Field Engineers (have routes)"
                        >
                          <span class="icon-[material-symbols--person]" style="font-size: 16px; color: #00bcd4;"></span>
                          <span><strong>${stats.totalAssignedInternalFEs}</strong> assigned internal FEs</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Available partner / external Field Engineers"
                        >
                          <span class="icon-[material-symbols--person]" style="font-size: 16px; color: #9c27b0;"></span>
                          <span><strong>${stats.totalAvailablePartnerFEs}</strong> available partner FEs</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Assigned partner / external Field Engineers (have routes)"
                        >
                          <span class="icon-[material-symbols--person]" style="font-size: 16px; color: #9c27b0;"></span>
                          <span><strong>${stats.totalAssignedPartnerFEs}</strong> assigned partner FEs</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Total Unavailable Field Engineers for Optimization but with routes"
                        >
                          <span class="icon-[material-symbols--person]" style="font-size: 16px; color: #00bcd4;"></span>
                          <span><strong>${stats.totalUnavailableFEsWithRoutes}</strong> additional FEs</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Total assigned Field Engineers"
                        >
                          <span class="icon-[material-symbols--person]" style="font-size: 16px; color: #00bcd4;"></span>
                          <span><strong>${stats.totalFesWithRoutes}</strong> assigned FEs</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Total Tickets scheduled (with assigned FE) - ${stats.totalImacTickets} IMAC | ${stats.totalHesTickets} HES | ${stats.totalEntTickets} ENT | ${stats.totalClientTickets} Client | ${stats.totalUnknownTicketTypeTickets} Unknown"
                        >
                          <span class="icon-[material-symbols--sell]" style="font-size: 16px; color: #e53935;"></span>
                          <span><strong>${stats.totalTickets}</strong> Tickets</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Total tickets considered for scheduling by Nexus (assigned + unassigned) - ${stats.totalTickets} assigned | ${unassignedTickets.length} unassigned"
                        >
                          <span class="icon-[material-symbols--sell]" style="font-size: 16px; color: #b39ddb;"></span>
                          <span><strong>${stats.totalTickets + unassignedTickets.length}</strong> All Tickets</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Tickets newly assigned by Nexus"
                        >
                          <span
                            class="icon-[material-symbols--playlist-add]"
                            style="font-size: 16px; color: #ff9800;"
                          ></span>
                          <span><strong>${selectedPlanning?.numNewlyAssignedTickets ?? 0}</strong> New</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Number of tickets that fell into the nexus ticket filter"
                        >
                          <span
                            class="icon-[material-symbols--filter-alt]"
                            style="font-size: 16px; color: #9c27b0;"
                          ></span>
                          <span><strong>${selectedPlanning?.numTicketsInFilter ?? 0}</strong> In Filter</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Total travel time for all routes"
                        >
                          <span
                            class="icon-[material-symbols--directions-car]"
                            style="font-size: 16px; color: #1e90ff;"
                          ></span>
                          <span>${formatTravelTime(stats.totalTravelMinutes)}</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Total service time for all tickets"
                        >
                          <span class="icon-[material-symbols--build]" style="font-size: 16px; color: #4caf50;"></span>
                          <span>${formatTravelTime(stats.totalServiceMinutes)}</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Free assignable working time across all available internal (non-partner) FEs: available working time minus used route minutes (travel + service)"
                        >
                          <span
                            class="icon-[material-symbols--schedule]"
                            style="font-size: 16px; color: #26a69a;"
                          ></span>
                          <span>${formatTravelTime(freeWorkingTime.allInternalFes)} free (all internal FEs)</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Free assignable working time only for internal FEs that have a route: available working time minus used route minutes (travel + service)"
                        >
                          <span
                            class="icon-[material-symbols--schedule]"
                            style="font-size: 16px; color: #00897b;"
                          ></span>
                          <span>${formatTravelTime(freeWorkingTime.usedInternalFes)} free (used internal FEs)</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Average traveled kilometers per field engineer (with ticket)"
                        >
                          <span
                            class="icon-[material-symbols--directions-car]"
                            style="font-size: 16px; color: #1e90ff;"
                          ></span>
                          <span>${formatDistanceKm(traveledKilometersPerFE)} / FE</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px;"
                          title="Accumulated travel kilometers per scheduled ticket"
                        >
                          <span
                            class="icon-[material-symbols--straighten]"
                            style="font-size: 16px; color: #9c27b0;"
                          ></span>
                          <span>${formatDistanceKm(accumulatedKilometersPerTicket)} / Ticket</span>
                        </div>
                        <div
                          style="display: flex; align-items: center; gap: 6px; cursor: pointer;"
                          title="Click for detailed profitability breakdown"
                          @click=${() =>
                            setProfitabilityBreakdown({
                              name: 'Total Profitability',
                              profitability: totalProfitabilitySummary,
                              ticketTypeBreakdown: totalProfitabilitySummary.ticketTypeBreakdown,
                              availableTimeMinutes: totalProfitabilitySummary.scheduleMinutes,
                              isTotal: true,
                            })}
                        >
                          <span
                            class="icon-[material-symbols--trending-up]"
                            style="font-size: 16px; color: ${totalProfitability >= 0 ? '#4caf50' : '#e53935'};"
                          ></span>
                          <span
                            style="color: ${totalProfitability >= 0
                              ? '#4caf50'
                              : '#e53935'}; text-decoration: underline;"
                            >${formatCurrency(totalProfitability)} Profitability</span
                          >
                        </div>
                        ${selectedPlanning?.unscheduledTickets?.length
                          ? html`<div
                              style="display: flex; align-items: center; gap: 6px; cursor: pointer; padding: 4px 8px; border-radius: 4px; background: rgba(255, 152, 0, 0.15); white-space: nowrap;"
                              @click=${() => setShowUnassignedDialog(true)}
                              title="Click to view unassigned tickets"
                            >
                              <span
                                class="icon-[material-symbols--warning]"
                                style="font-size: 16px; color: #ff9800;"
                              ></span>
                              <span style="color: #ff9800;"
                                ><strong>${unassignedTickets.length}</strong> Unassigned</span
                              >
                            </div>`
                          : ''}
                        ${selectedPlanning?.fourHourSlaTickets?.length
                          ? html`<div
                              style="display: flex; align-items: center; gap: 6px; cursor: pointer; padding: 4px 8px; border-radius: 4px; background: rgba(255, 152, 0, 0.15); white-space: nowrap;"
                              @click=${() => setShowFourHourSlaDialog(true)}
                              title="Click to view 4h SLA tickets"
                            >
                              <span
                                class="icon-[material-symbols--timer]"
                                style="font-size: 16px; color: #ff9800;"
                              ></span>
                              <span style="color: #ff9800;"><strong>${fourHourSlaTickets.length}</strong> 4h SLA</span>
                            </div>`
                          : ''}
                        ${fieldEngineersWithoutRoutes.length
                          ? html`<div
                              style="display: flex; align-items: center; gap: 6px; cursor: pointer; padding: 4px 8px; border-radius: 4px; background: rgba(255, 152, 0, 0.15); white-space: nowrap;"
                              @click=${() => setShowFieldEngineersWithoutRoutesDialog(true)}
                              title="Click to view field engineers without tickets"
                            >
                              <span
                                class="icon-[material-symbols--person-off]"
                                style="font-size: 16px; color: #ff9800;"
                              ></span>
                              <span style="color: #ff9800;"
                                ><strong>${fieldEngineersWithoutRoutes.length}</strong> No tickets</span
                              >
                            </div>`
                          : ''}
                        ${unprofitableFieldEngineers.length
                          ? html`<div
                              style="display: flex; align-items: center; gap: 6px; cursor: pointer; padding: 4px 8px; border-radius: 4px; background: rgba(255, 152, 0, 0.15); white-space: nowrap;"
                              @click=${() => setShowUnprofitableFieldEngineersDialog(true)}
                              title="Click to view unprofitable field engineers"
                            >
                              <span
                                class="icon-[material-symbols--trending-down]"
                                style="font-size: 16px; color: #ff9800;"
                              ></span>
                              <span style="color: #ff9800;"
                                ><strong>${unprofitableFieldEngineers.length}</strong> Unprofitable</span
                              >
                            </div>`
                          : ''}
                      </div>

                      <!-- Per-Area Stats -->
                      <div style="border-top: 1px solid #444; padding-top: 8px;">
                        <div
                          style="font-size: 10px; font-weight: 600; color: #666; margin-bottom: 6px; text-transform: uppercase;"
                        >
                          By Area
                        </div>
                        <div style="display: flex; flex-direction: column; gap: 6px;">
                          ${stats.areaStats.map(
                            area => html`
                              <div
                                style="display: flex; justify-content: space-between; align-items: center; font-size: 11px;"
                              >
                                <span style="font-weight: 500; color: #ccc;">${area.area}</span>
                                <div style="display: flex; gap: 8px; color: #888;">
                                  <span title="Field Employees"
                                    ><span
                                      class="icon-[material-symbols--person]"
                                      style="font-size: 12px; color: #00bcd4;"
                                    ></span>
                                    ${area.feCount}</span
                                  >
                                  <span title="Tickets"
                                    ><span
                                      class="icon-[material-symbols--sell]"
                                      style="font-size: 12px; color: #e53935;"
                                    ></span>
                                    ${area.ticketCount}</span
                                  >
                                  <span title="Travel Time"
                                    ><span
                                      class="icon-[material-symbols--directions-car]"
                                      style="font-size: 12px; color: #1e90ff;"
                                    ></span>
                                    ${formatTravelTime(area.totalTravelMinutes)}</span
                                  >
                                </div>
                              </div>
                            `,
                          )}
                        </div>
                      </div>

                      ${(() => {
                        const outliers = (selectedPlanning?.routes ?? [])
                          .map((route, idx) => {
                            const totalMinutes = getTotalTravelTime(route.stops) + getTotalServiceTime(route.stops);
                            return {
                              route,
                              idx,
                              totalMinutes,
                              name: route.vehicleName || route.vehicleId || 'Unknown',
                            };
                          })
                          .filter(o => o.totalMinutes > 8.5 * 60)
                          .sort((a, b) => b.totalMinutes - a.totalMinutes);
                        if (!outliers.length) return '';
                        return html`
                          <div style="border-top: 1px solid #444; padding-top: 8px; margin-top: 8px;">
                            <div
                              style="font-size: 10px; font-weight: 600; color: #ff4444; margin-bottom: 6px; text-transform: uppercase;"
                            >
                              Above 10h (${outliers.length}):
                            </div>
                            <div style="display: flex; flex-direction: column; gap: 4px;">
                              ${outliers.map(
                                o => html`
                                  <div
                                    style="display: flex; justify-content: space-between; align-items: center; font-size: 11px; cursor: pointer; padding: 4px 6px; border-radius: 4px; background: rgba(255, 68, 68, 0.1);"
                                    @click=${() => {
                                      setSelectedRoute(o.idx);
                                      setExpandedRoute(o.idx);
                                      scrollToRoute(o.idx);
                                    }}
                                  >
                                    <span style="font-weight: 500; color: #ccc;">${o.name}</span>
                                    <span style="color: #ff4444; font-weight: 600;"
                                      >${formatTravelTime(o.totalMinutes)}</span
                                    >
                                  </div>
                                `,
                              )}
                            </div>
                          </div>
                        `;
                      })()}
                    </div>
                  `;
                })()
              : ''}
            ${routePlanningsOverviewQuery.loading || (selectedPlanningId ? selectedPlanningQuery.loading : false)
              ? html`<div style="color: #aaa; font-size: 14px; display: flex; align-items: center; gap: 8px;">
                  <span class="loading loading-spinner loading-sm"></span>
                  Loading routes...
                </div>`
              : routePlanningsOverviewQuery.error || selectedPlanningQuery.error
                ? html`<div style="color: #e53935; font-size: 14px;">
                    Error loading routes: ${(routePlanningsOverviewQuery.error ?? selectedPlanningQuery.error)?.message}
                  </div>`
                : !selectedPlanning ||
                    (selectedPlanning.routes.length === 0 && fieldEngineersWithExternalTickets.length === 0)
                  ? html`<div style="color: #aaa; font-size: 14px;">
                      No routes available. Routes are automatically optimized daily at 3 PM and 6 PM.
                    </div>`
                  : (() => {
                      const groupedRoutes = groupRoutesByArea(selectedPlanning.routes);
                      return [
                        ...Array.from(groupedRoutes.entries()).map(
                          ([area, areaRoutes]) => html`
                            <div style="margin-bottom: 16px;">
                              <div
                                style="font-size: 12px; font-weight: 600; color: #888; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;"
                              >
                                ${area} (${areaRoutes.length})
                              </div>
                              ${areaRoutes.map(({ route, originalIndex: idx }) => {
                                const routeEngineerSchedule =
                                  (route.vehicleId
                                    ? fieldEngineerScheduleByKey.get(String(route.vehicleId).trim().toLowerCase())
                                    : undefined) ??
                                  (route.vehicleName
                                    ? fieldEngineerScheduleByKey.get(String(route.vehicleName).trim().toLowerCase())
                                    : undefined);
                                const workingTimeMinutes = getWorkingTimeMinutes(
                                  routeEngineerSchedule?.startTime,
                                  routeEngineerSchedule?.endTime,
                                );
                                const fullWorkingTimeMinutes = getWorkingTimeMinutes(
                                  routeEngineerSchedule?.theoreticalStartTime,
                                  routeEngineerSchedule?.theoreticalEndTime,
                                );
                                const totalRouteTimeMinutes =
                                  getTotalTravelTime(route.stops) + getTotalServiceTime(route.stops);
                                const profitability = getEngineerProfitabilitySummary(
                                  {
                                    id: route.vehicleId,
                                    name: route.vehicleName,
                                    startTime: routeEngineerSchedule?.startTime,
                                    endTime: routeEngineerSchedule?.endTime,
                                    theoreticalStartTime: routeEngineerSchedule?.theoreticalStartTime,
                                    theoreticalEndTime: routeEngineerSchedule?.theoreticalEndTime,
                                  },
                                  routeRevenueByEngineer,
                                  selectedPlanning,
                                );
                                return html`
                                  <div
                                    data-route-idx=${idx}
                                    style="
                                  background: ${selectedRoute === idx ? '#2d2d2d' : 'transparent'};
                                  border: 2px solid ${totalRouteTimeMinutes > 600
                                      ? '#ff4444'
                                      : selectedRoute === idx
                                        ? route.color
                                        : 'transparent'};
                                  border-radius: 8px;
                                  padding: 12px;
                                  margin-bottom: 8px;
                                  cursor: pointer;
                                  transition: all 0.2s;
                                "
                                    @click=${() => {
                                      // If clicking on already selected route, deselect it
                                      if (selectedRoute === idx) {
                                        setSelectedRoute(null);
                                        setExpandedRoute(null);
                                      } else {
                                        setSelectedRoute(idx);
                                        setExpandedRoute(idx);
                                        scrollToRoute(idx);
                                      }
                                    }}
                                  >
                                    <div
                                      style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;"
                                    >
                                      <div style="display: flex; align-items: center; gap: 8px;">
                                        <div
                                          style="width: 12px; height: 12px; background: ${route.color}; border-radius: 2px;"
                                        ></div>
                                        <div style="display: flex; flex-direction: column;">
                                          <div style="display: flex; align-items: center; gap: 8px;">
                                            <span style="font-weight: 600;"
                                              >${route.vehicleName || route.vehicleId}</span
                                            >
                                            ${profitability.isPartnerFieldEngineer
                                              ? html`<span
                                                  style="font-size: 10px; padding: 1px 6px; border-radius: 4px; background: #9c27b0; color: white;"
                                                  >Partner</span
                                                >`
                                              : ''}
                                          </div>
                                          ${route.vehicleName && route.vehicleId
                                            ? html`<span style="font-size: 10px; color: #666;"
                                                >${route.vehicleId}</span
                                              >`
                                            : ''}
                                        </div>
                                      </div>
                                    </div>
                                    <div style="font-size: 12px; color: #aaa;">
                                      <div style="display: flex; gap: 6px; flex-wrap: wrap;">
                                        <span style="white-space: nowrap;"
                                          >${(() => {
                                            if (!route.stops) return 0;
                                            return dedupeDepotStops(route.stops).length;
                                          })()}
                                          stops</span
                                        >
                                        ${fullWorkingTimeMinutes !== null
                                          ? html`<span
                                              style="display: flex; align-items: center; gap: 4px; white-space: nowrap;"
                                              title="FE working time"
                                            >
                                              <span
                                                class="icon-[material-symbols--badge]"
                                                style="font-size: 14px;"
                                              ></span>
                                              ${formatTravelTime(fullWorkingTimeMinutes)} working time
                                            </span>`
                                          : ''}
                                        ${workingTimeMinutes !== null
                                          ? html`<span
                                              style="display: flex; align-items: center; gap: 4px; white-space: nowrap;"
                                              title="FE working time"
                                            >
                                              <span
                                                class="icon-[material-symbols--badge]"
                                                style="font-size: 14px;"
                                              ></span>
                                              ${formatTravelTime(workingTimeMinutes)} assignable working time
                                            </span>`
                                          : ''}
                                      </div>
                                      <div style="display: flex; gap: 6px; flex-wrap: wrap; margin-top: 4px;">
                                        <span
                                          style="display: flex; align-items: center; gap: 4px; white-space: nowrap;"
                                          title="Total time (travel + service)"
                                        >
                                          <span
                                            class="icon-[material-symbols--schedule]"
                                            style="font-size: 14px;"
                                          ></span>
                                          ${formatTravelTime(
                                            getTotalTravelTime(route.stops) + getTotalServiceTime(route.stops),
                                          )}
                                        </span>
                                        <span
                                          style="display: flex; align-items: center; gap: 4px; white-space: nowrap;"
                                          title="Travel time"
                                        >
                                          <span
                                            class="icon-[material-symbols--directions-car]"
                                            style="font-size: 14px;"
                                          ></span>
                                          ${formatTravelTime(getTotalTravelTime(route.stops))}
                                        </span>
                                        <span
                                          style="display: flex; align-items: center; gap: 4px; white-space: nowrap;"
                                          title="Service time"
                                        >
                                          <span class="icon-[material-symbols--build]" style="font-size: 14px;"></span>
                                          ${formatTravelTime(getTotalServiceTime(route.stops))}
                                        </span>
                                        <span
                                          style="display: flex; align-items: center; gap: 4px; white-space: nowrap;"
                                          title="Traveled distance"
                                        >
                                          <span
                                            class="icon-[material-symbols--straighten]"
                                            style="font-size: 14px;"
                                          ></span>
                                          ${formatDistanceKm(getTotalTravelDistanceKm(route.stops))}
                                        </span>
                                      </div>
                                      <div style="display: flex; gap: 8px; margin-top: 4px; flex-wrap: wrap;">
                                        ${route.stops?.filter(s => s.type === 'ticket').length
                                          ? html`<span style="display: flex; align-items: center; gap: 2px;">
                                              <span
                                                class="icon-[material-symbols--sell]"
                                                style="font-size: 12px; color: ${colors.ticket.backgroundColor};"
                                              ></span>
                                              ${route.stops?.filter(
                                                s => s.type === 'ticket' && s.ticketType !== 'Other',
                                              ).length}
                                              ticket(s)
                                            </span>`
                                          : ''}
                                        ${(() => {
                                          const uniqueDepots = dedupeDepotStops(route.stops).filter(
                                            s => s.type === 'depot',
                                          );
                                          return uniqueDepots.length
                                            ? html`<span style="display: flex; align-items: center; gap: 2px;">
                                                <span
                                                  class="icon-[material-symbols--home]"
                                                  style="font-size: 12px; color: ${colors.depot.backgroundColor};"
                                                ></span>
                                                ${uniqueDepots.length} pudo(s)
                                              </span>`
                                            : '';
                                        })()}
                                      </div>
                                      <div style="display: flex; gap: 8px; margin-top: 4px; flex-wrap: wrap;">
                                        <span title="Ticket revenue"
                                          ><span style="font-weight: 600; color: #999;">Revenue</span> ${formatCurrency(
                                            profitability.ticketRevenue,
                                          )}</span
                                        >
                                        <span title="Adjusted FE day cost"
                                          ><span style="font-weight: 600; color: #999;">Cost</span> ${formatCurrency(
                                            profitability.adjustedFeCost,
                                          )}</span
                                        >
                                        <span
                                          title="Click for detailed breakdown"
                                          style="cursor: pointer;"
                                          @click=${(e: Event) => {
                                            e.stopPropagation();
                                            const typeBreakdown: Record<string, number> = {};
                                            for (const stop of route.stops ?? []) {
                                              if (stop.type === 'ticket' && stop.ticketType) {
                                                typeBreakdown[stop.ticketType] =
                                                  (typeBreakdown[stop.ticketType] ?? 0) + 1;
                                              }
                                            }
                                            setProfitabilityBreakdown({
                                              name: route.vehicleName || route.vehicleId || 'Unknown',
                                              id: route.vehicleId,
                                              profitability,
                                              ticketTypeBreakdown: typeBreakdown,
                                              availableTimeMinutes: fullWorkingTimeMinutes,
                                            });
                                          }}
                                        >
                                          <span style="font-weight: 600; color: #999;">Profitability</span>
                                          <span
                                            style="color: ${profitability.profitDelta >= 0
                                              ? '#4caf50'
                                              : '#e53935'}; text-decoration: underline;"
                                            >${formatCurrency(profitability.profitDelta)}</span
                                          ></span
                                        >
                                      </div>
                                    </div>

                                    ${expandedRoute === idx
                                      ? (() => {
                                          const dedupedStops = dedupeDepotStops(route.stops);
                                          return html`
                                            ${renderSidebarTicketSection(
                                              'IMAC Tickets',
                                              getImacTicketsForEngineer(selectedPlanning, route.vehicleId),
                                              fieldEngineerLabelById,
                                              'No IMAC tickets outside this route.',
                                            )}
                                            ${renderSidebarTicketSection(
                                              'Fixed FE Tickets',
                                              getFixedFETicketsForEngineer(selectedPlanning, route.vehicleId),
                                              fieldEngineerLabelById,
                                              'No fixed FE tickets for this engineer.',
                                            )}
                                            ${renderSidebarTicketSection(
                                              'Pre-Assigned Tickets',
                                              getAssignedTicketsForEngineer(selectedPlanning, route.vehicleId),
                                              fieldEngineerLabelById,
                                              'No pre-assigned tickets for this engineer.',
                                            )}
                                            <div
                                              style="margin-top: 12px; border-top: 1px solid #444; padding-top: 8px;"
                                            >
                                              ${dedupedStops.map((stop, stopIdx) => {
                                                const stopConfig =
                                                  colors[stop.type as keyof typeof colors] ?? colors.waypoint;
                                                return html`
                                                  <div
                                                    style="
                                                display: flex;
                                                gap: 8px;
                                                margin-bottom: 8px;
                                                font-size: 11px;
                                                align-items: flex-start;
                                              "
                                                  >
                                                    <div
                                                      style="
                                                  min-width: 20px;
                                                  height: 20px;
                                                  border-radius: 50%;
                                                  background: ${stopConfig.backgroundColor};
                                                  border: 2px solid ${stopConfig.borderColor};
                                                  display: flex;
                                                  align-items: center;
                                                  justify-content: center;
                                                  font-size: 10px;
                                                  font-weight: 600;
                                                  color: ${stopConfig.textColor};
                                                "
                                                    >
                                                      ${stop.type === 'ticket'
                                                        ? html`<span
                                                            class="icon-[material-symbols--sell]"
                                                            style="font-size: 12px;"
                                                          ></span>`
                                                        : stop.type === 'depot'
                                                          ? html`<span
                                                              class="icon-[material-symbols--home]"
                                                              style="font-size: 12px;"
                                                            ></span>`
                                                          : stop.type === 'employee'
                                                            ? html`<span
                                                                class="icon-[material-symbols--person]"
                                                                style="font-size: 12px;"
                                                              ></span>`
                                                            : stopIdx + 1}
                                                    </div>
                                                    <div style="flex: 1; line-height: 1.4;">
                                                      <div style="color: #fff; font-weight: 500;">
                                                        ${getStopAddress(stop, selectedPlanning) ?? 'Unknown'}
                                                      </div>
                                                      <div style="color: #888; font-size: 10px;">
                                                        ${stop.type === 'ticket' && stop.ticketType
                                                          ? stop.ticketId
                                                            ? html`<a
                                                                href=${`${ticketsBaseUrl}/otrs/index.pl?Action=AgentTicketZoom;TicketID=${encodeURIComponent(
                                                                  String(stop.ticketId),
                                                                )}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style="color: #2196f3; text-decoration: none;"
                                                                >${stop.type} (${stop.ticketType})</a
                                                              >`
                                                            : `${stop.type} (${stop.ticketType})`
                                                          : (stop.type ?? 'waypoint')}
                                                      </div>
                                                    </div>
                                                  </div>
                                                `;
                                              })}
                                            </div>
                                          `;
                                        })()
                                      : ''}
                                  </div>
                                `;
                              })}
                            </div>
                          `,
                        ),
                        ...(fieldEngineersWithExternalTickets.length
                          ? [
                              html`
                                <div style="margin-bottom: 16px;">
                                  <div
                                    style="font-size: 12px; font-weight: 600; color: #888; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;"
                                  >
                                    External Tickets (${fieldEngineersWithExternalTickets.length})
                                  </div>
                                  ${fieldEngineersWithExternalTickets.map(fe => {
                                    const workingTimeMinutes = getWorkingTimeMinutes(fe.startTime, fe.endTime);
                                    const uncappedWorkingTimeMinutes = getWorkingTimeMinutes(
                                      fe.startTime,
                                      fe.endTime,
                                      false,
                                    );
                                    const imacTicketsForEngineer = getImacTicketsForEngineer(selectedPlanning, fe.id);
                                    const fixedFETicketsForEngineer = getFixedFETicketsForEngineer(
                                      selectedPlanning,
                                      fe.id,
                                    );
                                    const otherAssignedTicketsForEngineer = getAssignedTicketsForEngineer(
                                      selectedPlanning,
                                      fe.id,
                                    );
                                    const profitability = getEngineerProfitabilitySummary(
                                      fe,
                                      routeRevenueByEngineer,
                                      selectedPlanning,
                                    );
                                    return html`
                                      <div
                                        style="
                                      background: #2d2d2d;
                                      border: 2px solid ${uncappedWorkingTimeMinutes !== null &&
                                        uncappedWorkingTimeMinutes > 600
                                          ? '#ff4444'
                                          : 'transparent'};
                                      border-radius: 8px;
                                      padding: 12px;
                                      margin-bottom: 8px;
                                    "
                                      >
                                        <div
                                          style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;"
                                        >
                                          <div style="display: flex; align-items: center; gap: 8px;">
                                            <div
                                              style="width: 12px; height: 12px; background: #ff9800; border-radius: 2px;"
                                            ></div>
                                            <div style="display: flex; flex-direction: column;">
                                              <span style="font-weight: 600;">${fe.name || fe.id}</span>
                                              ${fe.name && fe.id
                                                ? html`<span style="font-size: 10px; color: #666;">${fe.id}</span>`
                                                : ''}
                                            </div>
                                          </div>
                                        </div>
                                        <div style="font-size: 12px; color: #aaa;">
                                          <div style="display: flex; gap: 6px; flex-wrap: wrap;">
                                            ${workingTimeMinutes !== null
                                              ? html`<span
                                                  style="display: flex; align-items: center; gap: 4px; white-space: nowrap;"
                                                  title="FE working time"
                                                >
                                                  <span
                                                    class="icon-[material-symbols--badge]"
                                                    style="font-size: 14px;"
                                                  ></span>
                                                  ${formatTravelTime(workingTimeMinutes)} max working time
                                                </span>`
                                              : ''}
                                          </div>
                                          <div style="display: flex; gap: 8px; margin-top: 4px; flex-wrap: wrap;">
                                            ${imacTicketsForEngineer.length
                                              ? html`<span style="display: flex; align-items: center; gap: 2px;">
                                                  <span
                                                    class="icon-[material-symbols--sell]"
                                                    style="font-size: 12px; color: ${colors.ticket.backgroundColor};"
                                                  ></span>
                                                  ${imacTicketsForEngineer.length} IMAC ticket(s)
                                                </span>`
                                              : ''}
                                            ${fixedFETicketsForEngineer.length
                                              ? html`<span style="display: flex; align-items: center; gap: 2px;">
                                                  <span
                                                    class="icon-[material-symbols--lock-person]"
                                                    style="font-size: 12px; color: #ff9800;"
                                                  ></span>
                                                  ${fixedFETicketsForEngineer.length} fixed FE ticket(s)
                                                </span>`
                                              : ''}
                                          </div>
                                          <div style="display: flex; gap: 8px; margin-top: 4px; flex-wrap: wrap;">
                                            <span title="Ticket revenue"
                                              ><span style="font-weight: 600; color: #999;">Revenue</span>
                                              ${formatCurrency(profitability.ticketRevenue)}</span
                                            >
                                            <span title="Adjusted FE day cost"
                                              ><span style="font-weight: 600; color: #999;">Cost</span>
                                              ${formatCurrency(profitability.adjustedFeCost)}</span
                                            >
                                            <span
                                              title="Click for detailed breakdown"
                                              style="cursor: pointer;"
                                              @click=${(e: Event) => {
                                                e.stopPropagation();
                                                const typeBreakdown: Record<string, number> = {};
                                                for (const ticket of [
                                                  ...imacTicketsForEngineer,
                                                  ...fixedFETicketsForEngineer,
                                                  ...otherAssignedTicketsForEngineer,
                                                ]) {
                                                  if (ticket.type) {
                                                    typeBreakdown[ticket.type] = (typeBreakdown[ticket.type] ?? 0) + 1;
                                                  }
                                                }
                                                setProfitabilityBreakdown({
                                                  name: fe.name || fe.id || 'Unknown',
                                                  id: fe.id,
                                                  profitability,
                                                  ticketTypeBreakdown: typeBreakdown,
                                                  availableTimeMinutes: workingTimeMinutes,
                                                });
                                              }}
                                            >
                                              <span style="font-weight: 600; color: #999;">Profitability</span>
                                              <span
                                                style="color: ${profitability.profitDelta >= 0
                                                  ? '#4caf50'
                                                  : '#e53935'}; text-decoration: underline;"
                                                >${formatCurrency(profitability.profitDelta)}</span
                                              ></span
                                            >
                                          </div>
                                        </div>
                                        ${renderSidebarTicketSection(
                                          'IMAC Tickets',
                                          imacTicketsForEngineer,
                                          fieldEngineerLabelById,
                                          'No IMAC tickets for this engineer.',
                                        )}
                                        ${renderSidebarTicketSection(
                                          'Fixed FE Tickets',
                                          fixedFETicketsForEngineer,
                                          fieldEngineerLabelById,
                                          'No fixed FE tickets for this engineer.',
                                        )}
                                        ${renderSidebarTicketSection(
                                          'Other assigned Tickets',
                                          otherAssignedTicketsForEngineer,
                                          fieldEngineerLabelById,
                                          'No other assigned tickets for this engineer.',
                                        )}
                                      </div>
                                    `;
                                  })}
                                </div>
                              `,
                            ]
                          : []),
                      ];
                    })()}
          </div>

          <!-- Drag handle -->
          <div
            style="width: 4px; background: #333; cursor: col-resize; flex-shrink: 0; position: relative;"
            ${sidebarHandleDirective({ ariaLabel: 'Sidebar width divider' })}
          >
            <div
              style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 2px; height: 32px; background: #555; border-radius: 1px;"
            ></div>
          </div>

          <!-- Map -->
          <div style="flex: 1; position: relative;">
            ${!mapInitialized
              ? html`<div
                  style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: white; cursor: pointer; z-index: 10; background: rgba(0,0,0,0.8);"
                  @click=${() => setMapInitialized(true)}
                >
                  Click, to initialize!
                </div>`
              : ''}
            ${mapInitialized
              ? MapBox({
                  accessToken: mapboxAccessToken,
                  mapOptions: {
                    style: 'mapbox://styles/mapbox/dark-v11',
                    center: [2.2137, 46.2276],
                    zoom: 5,
                  },
                  height: '100%',
                  markerGroups: allMarkerGroups,
                  routes: mapRoutes,
                  highlightedRoute: selectedRoute ?? undefined,
                  customizeMap: (map: mapboxgl.Map) => {
                    map.on('click', (e: mapboxgl.MapMouseEvent) => {
                      const features = map.queryRenderedFeatures(e.point);
                      const routeFeature = features.find(f => f.layer?.id?.startsWith('route-'));
                      if (routeFeature?.layer?.id) {
                        const routeIndex = parseInt(routeFeature.layer.id.replace('route-', ''), 10);
                        if (!Number.isNaN(routeIndex)) {
                          routeClickRef.current(routeIndex);
                        }
                      }
                    });
                    map.on('mousemove', (e: mapboxgl.MapMouseEvent) => {
                      const features = map.queryRenderedFeatures(e.point);
                      const isOverRoute = features.some(f => f.layer?.id?.startsWith('route-'));
                      map.getCanvas().style.cursor = isOverRoute ? 'pointer' : '';
                    });
                  },
                })
              : ''}
          </div>
        </div>
      `;
    }),
  },
});
